import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { pageOf } from '@/shared/lib/address';
import { Link } from 'react-router';

import { RichText } from '@/music/text/RichText';
import { useGetCatalogQuery, useGetSharedQuery } from '@/shared/api/endpoints';
import { selectSignedIn } from '@/shared/api/sessionSlice';
import type { CatalogItem } from '@/shared/api/types';
import { useContentLocale } from '@/shared/lib/locale';
import { usePageTitle } from '@/shared/lib/usePageTitle';
import { Alert, Badge, PageTitle, SearchField, Spinner } from '@/shared/ui/primitives';
import { Pager } from '@/shared/ui/Pager';

/** Where an entry of the hub leads: a course to its map, a lesson straight to itself. */
const hrefOf = (item: CatalogItem) =>
  pageOf(item.id);

function CatalogCard({ item }: { item: CatalogItem }) {
  const { t } = useTranslation();
  const facts =
    item.kind === 'course'
      ? [
          t('hub.topics', { count: item.topicCount ?? 0 }),
          t('hub.lessons', { count: item.lessonCount ?? 0 }),
        ]
      : [];
  return (
    <Link
      to={hrefOf(item)}
      className="flex h-full flex-col rounded-3xl bg-surface p-6 shadow-xs transition-shadow hover:shadow-md"
    >
      <p className="mb-3 flex flex-wrap items-center gap-2">
        <Badge tone="info">{t(`hub.kind.${item.kind}`)}</Badge>
        {item.author.official ? (
          <Badge tone="ok" icon="verified">
            {t('hub.official')}
          </Badge>
        ) : null}
      </p>
      <h2 className="hs-h3">
        <RichText text={item.title} hints="hover" />
      </h2>
      {item.description ? (
        <p className="mt-1 text-sm text-muted">
          <RichText text={item.description} hints="hover" />
        </p>
      ) : null}
      <p className="mt-auto pt-4 text-xs text-muted">
        {[...facts, t('hub.by', { name: item.author.name })].join(' · ')}
      </p>
    </Link>
  );
}

/**
 * The hub: everything published for everyone. A course is one card, whatever is in
 * it; a lesson that stands alone is a card of its own.
 */
export function HubPage() {
  const { t } = useTranslation();
  const lang = useContentLocale();
  const [search, setSearch] = useState('');
  usePageTitle(t('hub.title'));
  // Pages already passed: each entry is the cursor that opens the page after it.
  const [cursors, setCursors] = useState<string[]>([]);
  const query = search.trim();
  const catalog = useGetCatalogQuery({ lang, q: query || undefined, cursor: cursors.at(-1) });
  const items = catalog.data?.items;
  const next = catalog.data?.nextCursor;
  // Private things a teacher shared with this account: they are in nobody's hub. Without
  // an account nothing was shared with anybody.
  const signedIn = useSelector(selectSignedIn);
  const shared = useGetSharedQuery(lang, { skip: !signedIn });

  return (
    <>
      <PageTitle subtitle={t('hub.subtitle')}>{t('hub.title')}</PageTitle>
      {shared.data && shared.data.length > 0 ? (
        <section aria-label={t('hub.shared')} className="mb-10">
          <h2 className="mb-1 text-xl font-bold">{t('hub.shared')}</h2>
          <p className="mb-4 text-sm text-muted">{t('hub.sharedHint')}</p>
          <ul className="grid gap-4 sm:grid-cols-2">
            {shared.data.map((item) => (
              <li key={`${item.kind}:${item.id}`}>
                <CatalogCard item={item} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <SearchField
        label={t('hub.search')}
        placeholder={t('hub.search')}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setCursors([]);
        }}
        className="mb-6 max-w-md"
      />

      {catalog.isError ? <Alert tone="bad">{t('errors.loadFailed')}</Alert> : null}
      {!items && !catalog.isError ? <Spinner label={t('loading')} /> : null}
      {items?.length === 0 ? (
        <Alert>{t(query ? 'hub.nothingFound' : 'hub.empty')}</Alert>
      ) : null}

      <ul className="grid gap-4 sm:grid-cols-2">
        {items?.map((item) => (
          <li key={`${item.kind}:${item.id}`}>
            <CatalogCard item={item} />
          </li>
        ))}
      </ul>

      <Pager cursors={cursors} next={next} onChange={setCursors} labels={{ previous: t('hub.previous'), next: t('hub.next') }} />
    </>
  );
}
